import { atomStore, serviceSync } from "../services/sync.service";
import { FastDataCategoriesMap, FastDataConversationCategories, FastDataConversations, FastDataDraftMediaBlobUrls, FastDataDrafts, FastDataFailedSendingMap as FastDataFailedSendingMap, FastDataMediaBlobUrls, FastDataMessageLoadedTimestamps, FastDataMessages, FastDataMessageToConversation, FastDataUserCategorySubscriptions } from "./fast-data.types";

const THREAD_MESSAGES_PER_PAGE = 12;

export class FastData {
    private conversations: FastDataConversations = {};
    private messages: FastDataMessages = {};
    private messageToConversation: FastDataMessageToConversation = {};
    private timestamps: FastDataMessageLoadedTimestamps = {};
    private drafts: FastDataDrafts = {};
    private failedMessages: FastDataFailedSendingMap = {};
    private sendingMessages: FastDataFailedSendingMap = {};
    private mediaBlobUrls: FastDataMediaBlobUrls = {};
    private draftMediaBlobUrls: FastDataDraftMediaBlobUrls = {};
    private categories: FastDataCategoriesMap = {};
    private conversationCategories: FastDataConversationCategories = {};
    private userCategorySubscriptions: FastDataUserCategorySubscriptions = {};

    async initialLoad() {
        await this.reloadConversations();
        this.messages = {};
        this.messageToConversation = {};
        this.timestamps = {};
        this.drafts = {};
        this.draftMediaBlobUrls = {};
        this.failedMessages = {};
        this.sendingMessages = {};
        this.conversationCategories = {};
        this.userCategorySubscriptions = {};

        for (const message of await serviceSync.db.messages.orderBy("timestamp").toArray()) {
            this.messages[message.conv_id] = this.messages[message.conv_id] || [];
            this.messages[message.conv_id].push(message);
            this.messageToConversation[message.id] = message.conv_id;
        }
        for (const [conv_id, messages] of Object.entries(this.messages)) {
            const windowFirstIndex = Math.max(0, messages.length - THREAD_MESSAGES_PER_PAGE);
            this.timestamps[conv_id] = messages[windowFirstIndex].timestamp;
            messages.splice(0, windowFirstIndex);
            for (const message of messages) {
                if (message.media && !this.mediaBlobUrls[message.media]) {
                    const mediaBlob = await serviceSync.db.mediaBlobs.get(message.media);
                    if (mediaBlob) {
                        this.mediaBlobUrls[message.media] = {
                            type: mediaBlob.type,
                            url: mediaBlob.url,
                            aspectRatio: mediaBlob.aspectRatio,
                            blobUrl: URL.createObjectURL(mediaBlob.blob),
                        };
                    }
                }
            }
        }
        for (const { conv_id, text, mediaBlobIds } of await serviceSync.db.drafts.toArray()) {
            this.drafts[conv_id] = {
                text,
                mediaBlobIds
            };
        }
        for (const { id } of await serviceSync.db.failedMessages.toArray()) {
            this.failedMessages[id] = true;
        }
        for (const { id } of await serviceSync.db.sendingMessages.toArray()) {
            this.sendingMessages[id] = true;
        }
        for (const { id, blob, ext } of await serviceSync.db.draftMediaBlobs.toArray()) {
            this.draftMediaBlobUrls[id!] = {
                blob,
                ext,
                url: URL.createObjectURL(blob),
            };
        }
        await this.reloadCategories();

        const convCategories = await serviceSync.db.conversation_categories.toArray();
        for (const cc of convCategories) {
            this.conversationCategories[cc.conv_id] = cc.category_id;
        }

        const userSubs = await serviceSync.db.user_category_subscriptions.toArray();
        for (const sub of userSubs) {
            if (!this.userCategorySubscriptions[sub.user_id]) {
                this.userCategorySubscriptions[sub.user_id] = [];
            }
            if (!this.userCategorySubscriptions[sub.user_id].includes(sub.category_id)) {
                this.userCategorySubscriptions[sub.user_id].push(sub.category_id);
            }
        }
    }

    async reloadConversations() {
        this.conversations = {};
        for (const conversation of await serviceSync.db.conversations.toArray()) {
            this.conversations[conversation.id] = conversation;
        }
    }

    getConversations() {
        return this.conversations;
    }

    getConversation(id: string) {
        return this.conversations[id];
    }

    getConversationIdFromMessage(id: string) {
        return this.messageToConversation[id];
    }

    getMessages() {
        return this.messages;
    }

    getMediaBlobUrl(url: string) {
        return this.mediaBlobUrls[url];
    }

    getDraftMediaBlob(id: number) {
        return this.draftMediaBlobUrls[id];
    }

    async reloadMessages(conv_id: string, expandOneWindow = false) {
        let wasExpanded = false;
        const messages = this.messages[conv_id] = await serviceSync.db.messages.where("conv_id").equals(conv_id).sortBy("timestamp");
        console.log(messages.map(({ text, timestamp }) => ({ text, timestamp })));
        if (messages.length === 0) {
            delete this.timestamps[conv_id];
            return;
        }
        for (const message of messages) {
            this.messageToConversation[message.id] = message.conv_id;
        }
        let timestamp = (typeof this.timestamps[conv_id] === "number") ? this.timestamps[conv_id] : messages[Math.max(0, messages.length - THREAD_MESSAGES_PER_PAGE)].timestamp;
        let windowFirstIndex = messages.findIndex(x => x.timestamp >= timestamp);
        if (windowFirstIndex === -1) {
            windowFirstIndex = messages.length;
            expandOneWindow = true;
        }
        if (expandOneWindow) {
            let beforeWindowFirstIndex = windowFirstIndex;
            windowFirstIndex = Math.max(0, windowFirstIndex - THREAD_MESSAGES_PER_PAGE);
            wasExpanded = windowFirstIndex < beforeWindowFirstIndex;
        }
        messages.splice(0, windowFirstIndex);
        for (const message of messages) {
            if (message.media && !this.mediaBlobUrls[message.media]) {
                const mediaBlob = await serviceSync.db.mediaBlobs.get(message.media);
                if (mediaBlob) {
                    this.mediaBlobUrls[message.media] = {
                        type: mediaBlob.type,
                        url: mediaBlob.url,
                        aspectRatio: mediaBlob.aspectRatio,
                        blobUrl: URL.createObjectURL(mediaBlob.blob),
                    };
                }
            }
        }
        this.timestamps[conv_id] = messages[0].timestamp;
        return wasExpanded;
    }

    async addDraftMediaBlob(blob: Blob, ext: string) {
        const id = await serviceSync.db.draftMediaBlobs.add({
            blob,
            ext
        });
        this.draftMediaBlobUrls[id] = {
            blob,
            ext,
            url: URL.createObjectURL(blob),
        };
        return id;
    }

    async removeDraftMediaBlob(id: number) {
        if (this.draftMediaBlobUrls[id]) {
            URL.revokeObjectURL(this.draftMediaBlobUrls[id].url);
            delete this.draftMediaBlobUrls[id];
        }
        await serviceSync.db.draftMediaBlobs.delete(id);
    }

    async updateDraft(conv_id: string, text: string, mediaBlobIds: number[]) {
        text = text.trim();
        if (text !== "" || mediaBlobIds.length > 0) {
            this.drafts[conv_id] = {
                text,
                mediaBlobIds: [...mediaBlobIds],
            };
            await serviceSync.db.drafts.put({ conv_id, text, mediaBlobIds });
        }
        else {
            delete this.drafts[conv_id];
            await serviceSync.db.drafts.delete(conv_id);
        }
    }

    getDraft(conv_id: string) {
        return this.drafts[conv_id];
    }

    async setFailedMessageStatus(id: string, status: boolean): Promise<undefined>;
    async setFailedMessageStatus(id: string, status: boolean, delayPersistence: true): Promise<() => Promise<void>>;
    async setFailedMessageStatus(id: string, status: boolean, delayPersistence = false) {
        if (status) {
            this.failedMessages[id] = true;
            const persist = async () => {
                await serviceSync.db.failedMessages.put({ id });
            };
            if (delayPersistence) {
                return persist;
            }
            else {
                await persist();
            }
        }
        else {
            delete this.failedMessages[id];
            const persist = async () => {
                await serviceSync.db.failedMessages.delete(id);
            };
            if (delayPersistence) {
                return persist;
            }
            else {
                await persist();
            }
        }
    }

    getFailedMessageStatus(id: string) {
        return !!this.failedMessages[id];
    }

    async setSendingMessageStatus(id: string, status: boolean): Promise<undefined>;
    async setSendingMessageStatus(id: string, status: boolean, delayPersistence: true): Promise<() => Promise<void>>;
    async setSendingMessageStatus(id: string, status: boolean, delayPersistence = false) {
        if (status) {
            this.sendingMessages[id] = true;
            const persist = async () => {
                await serviceSync.db.sendingMessages.put({ id });
            };
            if (delayPersistence) {
                return persist;
            }
            else {
                await persist();
            }
        }
        else {
            delete this.sendingMessages[id];
            const persist = async () => {
                await serviceSync.db.sendingMessages.delete(id);
            };
            if (delayPersistence) {
                return persist;
            }
            else {
                await persist();
            }
        }
    }

    getSendingMessageStatus(id: string) {
        return !!this.sendingMessages[id];
    }

    async reloadCategories() {
        this.categories = (await serviceSync.db.meta.get("categories"))?.value || {};
    }

    getCategories(): FastDataCategoriesMap {
        return this.categories;
    }

    getConversationCategory(conv_id: string): string {
        return this.conversationCategories[conv_id] || "";
    }

    async setConversationCategory(conv_id: string, category_id: string): Promise<void> {
        if (category_id === "") {
            delete this.conversationCategories[conv_id];
            await serviceSync.db.conversation_categories.delete(conv_id);
        } else {
            this.conversationCategories[conv_id] = category_id;
            await serviceSync.db.conversation_categories.put({ conv_id, category_id });
        }
    }

    getUserCategorySubscriptions(user_id: string): string[] {
        return this.userCategorySubscriptions[user_id] || [];
    }

    async toggleUserCategorySubscription(user_id: string, category_id: string, state: "ON" | "OFF"): Promise<void> {
        if (state === "ON") {
            await serviceSync.db.user_category_subscriptions.put({ user_id, category_id });
            if (!this.userCategorySubscriptions[user_id]) {
                this.userCategorySubscriptions[user_id] = [];
            }
            if (!this.userCategorySubscriptions[user_id].includes(category_id)) {
                this.userCategorySubscriptions[user_id].push(category_id);
            }
        } else { // state === "OFF"
            await serviceSync.db.user_category_subscriptions.delete([user_id, category_id]);
            if (this.userCategorySubscriptions[user_id]) {
                this.userCategorySubscriptions[user_id] = this.userCategorySubscriptions[user_id].filter(cid => cid !== category_id);
                if (this.userCategorySubscriptions[user_id].length === 0) {
                    delete this.userCategorySubscriptions[user_id];
                }
            }
        }
    }
}